// Pure display formatting, shared by the pages (dates were previously duplicated
// per page). Stat values follow the dataviz house rules: full grouped digits while
// the number is scannable, auto-compact (12.9K / 4.2M) once it isn't.
//
// formatStatValue / formatUsdTotal moved to @recruiter/shared (as
// formatCompactCount / formatUsdTotal) when the launch-day dashboard's chart
// primitives were extracted into shared components, so a BI chart reads
// numbers the same way. Re-exported here under the original name so every
// existing `formatStatValue` call site in this app keeps working unchanged.
export { formatCompactCount as formatStatValue, formatUsdTotal } from "@recruiter/shared";

/** Date-only cell ("-" when missing or unparseable). Locale-formatted. */
export function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
}

/** Date+time cell ("-" when missing or unparseable). Locale-formatted. */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

/** Clock time only ("14:32"), for a freshness stamp beside data that was
 *  measured minutes ago rather than on this request.
 *
 *  Deliberately not `formatDateTime`: a cached-a-few-minutes-ago figure is
 *  read as "how recent is this", and a full date in front of the time makes
 *  the reader parse a sentence to answer that. The DATE is still available on
 *  hover via the element's `title`, for the one case where it is not today.
 *  24-hour and locale-fixed so the line has the same width in every browser. */
export function formatClock(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** Minutes between two timestamps, or null when either is missing, either is
 *  unparseable, or `to` precedes `from`.
 *
 *  Out-of-order stamps answer null rather than a negative duration: clock skew
 *  between the gateway and us is real, and "-14m to reply" is worse than
 *  admitting we do not know. */
export function minutesBetween(from: string | null, to: string | null): number | null {
  if (!from || !to) return null;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  return (end - start) / 60_000;
}

/** A duration, humanized: 42m, 3.5h, 2.1d. Null renders as a dash, never "0m" —
 *  an unmeasured wait is unknown, and "0m" would read as instant. Mirrors how
 *  the Talent dashboard prints its response times. */
export function formatMinutes(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "-";
  if (value < 60) return `${Math.round(value)}m`;
  if (value < 60 * 24) return `${(value / 60).toFixed(1)}h`;
  return `${(value / 1440).toFixed(1)}d`;
}

/** Whole-dollar money from integer cents ("-" when absent). Currency is printed
 *  beside the figure only when it is not USD, so the common case stays terse and
 *  a EUR offer can never be mistaken for a dollar one.
 *
 *  `maxCents` is the high end when the talent quoted a RANGE (`price_max_cents`,
 *  migration 0157) — both ends print then, because `cents` alone is only the
 *  bottom of it and an operator reading one end as the price is the same defect
 *  the client had (Monday 3156054660). Null/absent keeps the single figure. */
export function formatOfferPrice(
  cents: number | null,
  currency: string | null,
  maxCents: number | null = null,
): string {
  if (cents === null || !Number.isFinite(cents)) return "-";
  const code = (currency ?? "USD").toUpperCase();
  const money = (value: number): string => {
    const amount = (value / 100).toLocaleString("en-US", { maximumFractionDigits: 0 });
    return code === "USD" ? `$${amount}` : `${amount} ${code}`;
  };
  return maxCents !== null && Number.isFinite(maxCents) && maxCents > cents
    ? `${money(cents)} - ${money(maxCents)}`
    : money(cents);
}

/** What the client committed to spend, as the project tables print it. */
export interface ProjectBudget {
  minCents: number | null;
  maxCents: number | null;
  /** An HOURLY rate, in cents PER HOUR — the legacy slot (0112), superseded by
   *  `rateAmountCents` + `rateUnit: "hour"`. Kept separate from the fixed
   *  bounds and never folded into them: $100/h became a fake $8,000 total once
   *  already (Monday 3127191511). */
  hourlyCents: number | null;
  currency: string;
  /** The engagement structure (0156): the committed RATE in the client's own
   *  unit, plus the stated workload cadence. All optional so pre-0156 payload
   *  literals (and the CS listing, which never carries budget) still typecheck;
   *  the backend sends them on every budget-carrying listing. */
  rateAmountCents?: number | null;
  rateUnit?: "hour" | "day" | "week" | "month" | "year" | "deliverable" | null;
  rateDeliverableNoun?: string | null;
  ratePerPerson?: boolean;
  cadenceCount?: number | null;
  cadenceUnit?: "week" | "month" | "year" | null;
  /** The LOW ends of a stated rate band / workload band (migration 0208):
   *  "$50-80/hour, 10-20 hours a week". Display only: the sort figure and
   *  every alert judge the high end, like `recruiter.budget_view`. */
  rateAmountMinCents?: number | null;
  cadenceCountMin?: number | null;
}

const RATE_SUFFIX: Record<string, string> = {
  hour: "/h",
  day: "/day",
  week: "/wk",
  month: "/mo",
  year: "/yr",
};

/** The committed rate + its unit, or null when none is committed. The legacy
 *  hourly slot backs the rate as an hourly figure — same fallback the backend's
 *  `recruiter.budget_view` applies. */
function committedRate(budget: ProjectBudget): { cents: number; unit: string | null } | null {
  const rate = budget.rateAmountCents;
  if (rate != null && Number.isFinite(rate)) {
    return { cents: rate, unit: budget.rateUnit ?? null };
  }
  const hourly = budget.hourlyCents;
  if (hourly != null && Number.isFinite(hourly)) return { cents: hourly, unit: "hour" };
  return null;
}

/** ONE MONTH of the committed rate, in cents, or null when a month cannot be
 *  read: a monthly rate is the month, a weekly one is x4, a yearly one is one
 *  twelfth (floored to whole cents), and hour/day/
 *  deliverable rates need the stated workload (weekly cadence x4). No cadence
 *  means NO figure — inventing hours is the exact bug the split fields exist
 *  to prevent. Mirrors `recruiter.budget_view` (WEEKS_PER_MONTH = 4); change
 *  the two together or the console stops agreeing with the alerts. */
function monthlyEquivalentCents(budget: ProjectBudget): number | null {
  const rate = committedRate(budget);
  if (!rate) return null;
  if (rate.unit === "month") return rate.cents;
  if (rate.unit === "week") return rate.cents * 4;
  if (rate.unit === "year") return Math.floor(rate.cents / 12);
  const count = budget.cadenceCount;
  const per = budget.cadenceUnit;
  if (
    (rate.unit === "hour" || rate.unit === "day" || rate.unit === "deliverable") &&
    count != null &&
    Number.isFinite(count) &&
    count > 0 &&
    (per === "week" || per === "month" || per === "year")
  ) {
    // "100 hours a year" is a twelfth of a month's worth, floored — the same
    // reverse-of-annualizing a `year` RATE gets, mirroring
    // `recruiter.budget_view._monthly_equivalent_cents`.
    if (per === "year") return Math.floor((rate.cents * count) / 12);
    return rate.cents * count * (per === "week" ? 4 : 1);
  }
  return null;
}

/** The fixed half of the commitment as a cell fragment, or null. */
function fixedBudgetText(budget: ProjectBudget): string | null {
  const { minCents, maxCents, currency } = budget;
  const low = minCents !== null && Number.isFinite(minCents) ? minCents : null;
  const high = maxCents !== null && Number.isFinite(maxCents) ? maxCents : null;
  if (low !== null && high !== null) {
    // A range the client gave as one figure lands as min === max. Printing it
    // twice would read as a range they never stated.
    return low === high
      ? formatOfferPrice(low, currency)
      : `${formatOfferPrice(low, currency)} - ${formatOfferPrice(high, currency)}`;
  }
  if (high !== null) return `up to ${formatOfferPrice(high, currency)}`;
  if (low !== null) return `from ${formatOfferPrice(low, currency)}`;
  return null;
}

/** The rate half as a cell fragment ("$50/h, 20h/mo", "$200/video, 8/mo",
 *  "$500/mo per person"), or null. The rate prints AS COMMITTED — the derived
 *  monthly figure is the SORT value, never shown as though the client said it. */
function rateBudgetText(budget: ProjectBudget): string | null {
  const rate = committedRate(budget);
  if (!rate) return null;
  const suffix =
    rate.unit === "deliverable"
      ? `/${budget.rateDeliverableNoun || "unit"}`
      : rate.unit
        ? RATE_SUFFIX[rate.unit]
        : "";
  // A stated band prints both ends ("$50 - $80/h"); the legacy hourly slot has
  // no low end, and an inverted or equal pair prints the one figure.
  const rateMin = budget.rateAmountMinCents;
  const low =
    budget.rateAmountCents != null && rateMin != null && Number.isFinite(rateMin) && rateMin < rate.cents
      ? rateMin
      : rate.cents;
  let text = `${formatOfferPrice(low, budget.currency, rate.cents)}${suffix}`;
  if (budget.ratePerPerson) text += " per person";
  const count = budget.cadenceCount;
  const per = budget.cadenceUnit;
  // The workload, terse: "20h/mo", "8/wk", "10-20h/wk". Skipped for a month
  // rate, where it adds nothing to the money the cell answers for.
  if (count != null && Number.isFinite(count) && count > 0 && per && rate.unit !== "month") {
    const thing = rate.unit === "hour" ? "h" : "";
    const perLabel = per === "week" ? "wk" : per === "year" ? "yr" : "mo";
    const countMin = budget.cadenceCountMin;
    const countText =
      countMin != null && Number.isFinite(countMin) && countMin > 0 && countMin < count
        ? `${countMin}-${count}`
        : `${count}`;
    text += `, ${countText}${thing}/${perLabel}`;
  }
  return text;
}

/**
 * A committed budget in one cell.
 *
 * The real shapes, and they mean different things, so none is flattened into
 * another: a range / single figure / one-sided bound, a committed RATE in the
 * client's own unit (with its workload), or both halves of a hybrid joined
 * with " + ". "-" is NOT-CAPTURED, which is a fact an operator scans for and
 * must never read as zero.
 */
export function formatBudget(budget: ProjectBudget | null): string {
  if (!budget) return "-";
  const parts = [fixedBudgetText(budget), rateBudgetText(budget)].filter(
    (p): p is string => p !== null,
  );
  return parts.length > 0 ? parts.join(" + ") : "-";
}

/** The value the Budget column SORTS on, in cents: the committed total, or ONE
 *  MONTH of the committed rate — the same effective figure the big-client
 *  alerts judge (`recruiter.budget_view`), so the desk's sort order matches
 *  who actually paged it. A rate with no stated workload still has no monthly
 *  figure and sorts on the bare rate, among its own kind — an hourly ask has
 *  no total, and inventing one is the exact bug the split field exists to
 *  prevent. Null (not captured) sorts last in both directions, per the
 *  table's missing-value rule. */
export function budgetSortValue(budget: ProjectBudget | null): number | null {
  if (!budget) return null;
  const fixed = [budget.maxCents, budget.minCents].filter(
    (v): v is number => v !== null && Number.isFinite(v),
  )[0];
  const monthly = monthlyEquivalentCents(budget);
  if (fixed !== undefined && (monthly === null || fixed >= monthly)) return fixed;
  if (monthly !== null) return monthly;
  const rate = committedRate(budget);
  return rate ? rate.cents : null;
}

/** The delivery window the client asked for, as a table cell. A CEILING ("within
 *  N days"), not an estimate, so the header carries that and the cell stays
 *  terse. "-" when the client has not committed to a date. A stated window
 *  (migration 0208's `minDays`) prints both ends in the unit the ceiling picks
 *  ("2-3 weeks"); a low end that is missing, equal, or inverted prints the
 *  ceiling alone, never an invented spread. */
export function formatDeliveryDays(days: number | null, minDays: number | null = null): string {
  if (days === null || !Number.isFinite(days) || days <= 0) return "-";
  const unit = days < 14 ? "day" : days < 60 ? "week" : "month";
  const per = unit === "day" ? 1 : unit === "week" ? 7 : 30;
  const high = Math.round(days / per);
  const low =
    minDays !== null && Number.isFinite(minDays) && minDays > 0 && minDays < days
      ? Math.round(minDays / per)
      : high;
  const figure = low < high ? `${low}-${high}` : `${high}`;
  const plural = high === 1 && low === high ? unit : `${unit}s`;
  return `${figure} ${plural}`;
}

/** The timeline half of the engagement structure (0156), beside the one-off
 *  delivery ceiling. All committed facts off `search_preferences`; a plain
 *  one-off carries only `deliveryMaxDays` and renders exactly as before. */
export interface ProjectTimeline {
  deliveryMaxDays: number | null;
  /** The low end of a stated window (0208); the cell prints "2-3 weeks". */
  deliveryMinDays?: number | null;
  startDate?: string | null;
  durationKind?: "ongoing" | "fixed" | "trial_then_ongoing" | null;
  durationMonths?: number | null;
  /** The low end of a stated term (0208); the cell prints "3-6 months". */
  durationMonthsMin?: number | null;
}

/** "Sep 1" (with the year only when it is not this year) from an ISO date, or
 *  null when unparseable — a timeline cell fragment, not a full date cell. */
function shortDate(iso: string): string | null {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/**
 * The Timeline cell: an ongoing shape reads as its SHAPE + start ("Ongoing
 * from Sep 1", "Trial, then ongoing", "6 months"), a one-off keeps the
 * delivery ceiling ("within N days" semantics, terse), and a bare committed
 * start still shows. "-" stays NOT-CAPTURED, never "no deadline".
 */
export function formatTimeline(timeline: ProjectTimeline | null): string {
  if (!timeline) return "-";
  const start = timeline.startDate ? shortDate(timeline.startDate) : null;
  const from = start ? ` from ${start}` : "";
  switch (timeline.durationKind ?? null) {
    case "ongoing":
      return `Ongoing${from}`;
    case "trial_then_ongoing":
      return `Trial, then ongoing${from}`;
    case "fixed": {
      const months = timeline.durationMonths;
      const monthsMin = timeline.durationMonthsMin;
      const banded =
        months != null &&
        Number.isFinite(months) &&
        monthsMin != null &&
        Number.isFinite(monthsMin) &&
        monthsMin > 0 &&
        monthsMin < months;
      const label =
        months != null && Number.isFinite(months) && months > 0
          ? banded
            ? `${monthsMin}-${months} months`
            : `${months} month${months === 1 ? "" : "s"}`
          : "Fixed term";
      return `${label}${from}`;
    }
    default: {
      const days = formatDeliveryDays(timeline.deliveryMaxDays, timeline.deliveryMinDays ?? null);
      if (days !== "-") return days;
      return start ? `from ${start}` : "-";
    }
  }
}
